/**
 * RWBR — THE SEASON'S BOOKS, AND THE OFF-SEASON THAT SETTLES THEM.
 *
 * Thirty-six race weekends of `runRaceFinance`, then the four things Sagliano
 * only does once a year:
 *
 *     BONUS         one per driver, on final Cup rank (SAG/27).
 *     ROYALTIES     four factors, summed, divided by six, times salary — paid
 *                   to the driver AND matched by the team (SAG/27-28).
 *     RENEWAL       six factors decide whether each sponsorship survives the
 *                   winter (SAG/17-19).
 *     FAMILY WEALTH the owner's own money, if he has any (SAG/25).
 *
 * WHERE THE MONEY ACTUALLY IS. Nowhere in this file. Every function here
 * appends to a stream; `foldMoney` is the only thing that knows what anyone
 * has. `seasonFinanceReport` at the bottom is a fold and a formatter, not a
 * second set of books.
 *
 * FAMILY WEALTH IS ROLLED FIRST, at the top of the season, before a wheel
 * turns. SAG/25 never says when — "Roll 2d6 and check to see if the Team Owner
 * [has] 'deep pockets'" sits in the income section with no timing at all.
 *
 *   RULING (D-029 §8): once per season, pre-season. It is described as a
 *   standing property of the owner ("sufficient wealth to help keep Team
 *   afloat (for a while at least)"), not as a weekly event, and the
 *   one-team-per-owner restriction — "If a Team Owner has more than one Team,
 *   he can only use his personal fortune on the first one that is rolled" —
 *   only makes sense if the roll happens once, in a defined order.
 */

import { Dice } from '../dice.ts';
import type { Modifier } from '../ruleEvent.ts';
import type { DriverCard } from '../types.ts';
import { trackCard } from '../turn.ts';
import type { SeasonRaceResult, SeasonResult } from '../season.ts';
import type { SeasonStandings } from '../standings.ts';
import {
  driverAccount,
  foldMoney,
  moneyEvent,
  teamAccount,
  type MoneyEvent,
  type MoneyLedger,
} from './money.ts';
import {
  CITE_BONUS,
  CITE_DEEP_POCKETS_TYPO,
  CITE_FAMILY_WEALTH,
  CITE_ROYALTIES,
  CITE_TEAM_ROYALTIES,
  DEEP_POCKETS,
  DRIVER_SALARY_YEARLY,
  GLOOMY_ECONOMY_ROLL,
  hasDeepPockets,
  qualityTier,
  ROYALTY_DIVISOR,
  royaltyFanPoints,
  royaltyMediaPoints,
  royaltyPerformancePoints,
  royaltyTalentPoints,
  SAGLIANO_BASELINE_RACES,
  seasonBonusMb,
  SPONSORSHIP_YEARLY,
  type QualityTier,
} from './tables.ts';
import { CITE_PUBLIC_EYE, CITE_SPONSOR_MENTION, mentionFactorPoints, publicEyeFactorPoints } from './sponsorCodes.ts';
import { sponsorNameFor } from './sponsorNames.ts';
import {
  openingFinanceState,
  profileFromCard,
  raceFactsForNextWeek,
  runRaceFinance,
  type DriverFinanceState,
  type FinanceDriverProfile,
  type FinanceState,
  type PreviousRaceFacts,
  type RaceFinanceOptions,
  type RaceFinanceResult,
  type SponsorChange,
} from './raceFinance.ts';

// ---------------------------------------------------------------------------
// Input and output
// ---------------------------------------------------------------------------

export interface SeasonFinanceOptions extends RaceFinanceOptions {
  /**
   * Team owners, so SAG/25's "a Team Owner may own up to three teams… he can
   * only use his personal fortune on the first one that is rolled" can bite.
   * Without it every team is treated as separately owned, which is the
   * one-team-per-owner case and changes nothing.
   */
  ownerOf?: Record<string, string>;
}

export interface SeasonFinanceInput {
  season: SeasonResult;
  /** The roster as it stood at the start — the cards the season was priced off. */
  roster: readonly DriverCard[];
  /** driverId → teamId. Missing drivers are treated as independents. */
  teamOf?: Record<string, string>;
  options?: SeasonFinanceOptions;
  /**
   * D-EZ-91 — last winter's books: the sponsorship contracts that survived the
   * renewals, every shop's bank and every driver's own money. Omit for the
   * first season of a league.
   */
  carry?: FinanceCarry;
}

/**
 * D-EZ-91 — THE BOOKS THAT OUTLIVE A SEASON.
 *
 * The per-season ledger stays a per-season ledger — a P&L, folded from that
 * year's events — so every screen that shows "the year" still shows the year.
 * What carries is held here instead, and nowhere else:
 *
 *   contracts      every sponsorship after SAG/19's renewals, named, with tenure.
 *   shopBankMb     each OWNER's bank: the sum of every season's P&L across the
 *                  cars he ran. Keyed by shop id for a charter and by the car's
 *                  own Sagliano team id for an independent. This is what D-031's
 *                  fold test reads — its own comment always said "cumulative".
 *   driverWealthMb each driver's own money, season on season: salary, winnings,
 *                  royalties, bonus, less fines. Kept after he retires.
 */
export interface FinanceCarry {
  contracts: Record<string, DriverFinanceState>;
  shopBankMb: Record<string, number>;
  driverWealthMb: Record<string, number>;
  /** How many winters these books have been carried through. */
  winters: number;
}

export const EMPTY_FINANCE_CARRY: FinanceCarry = Object.freeze({
  contracts: {},
  shopBankMb: {},
  driverWealthMb: {},
  winters: 0,
}) as FinanceCarry;

export interface RoyaltyBreakdown {
  driverId: string;
  name: string;
  mediaPoints: number;
  fanPoints: number;
  talentPoints: number;
  performancePoints: number;
  total: number;
  factor: number;
  salaryMb: number;
  royaltiesMb: number;
  tvAppearances: number;
  leagueAverageAppearances: number;
}

export interface RenewalFactor {
  name: string;
  points: number;
  note: string;
  ruleCitation: string;
}

export interface RenewalDecision {
  driverId: string;
  name: string;
  tier: 'primary' | 'major' | 'associate';
  factors: RenewalFactor[];
  total: number;
  renewed: boolean;
  /** SAG/19 — Z54 renewed it regardless of the sum. */
  automatic: boolean;
  /** SAG/19 — a dropped primary is replaced at one quality lower. */
  replacedAtQualities?: QualityTier;
  description: string;
}

export interface SeasonFinanceResult {
  seasonId: string;
  events: MoneyEvent[];
  races: RaceFinanceResult[];
  royalties: RoyaltyBreakdown[];
  renewals: RenewalDecision[];
  /** Sponsor comings and goings during the season, in race order. */
  sponsorChanges: (SponsorChange & { raceNumber: number })[];
  /** Contract state as it stands going into next season. */
  state: FinanceState;
  /** SAG/18 factor 6 — was the economy gloomy in the season's last race? */
  gloomyEconomy: boolean;
  ledger: MoneyLedger;
  /** D-EZ-91 — the books as they go into the winter, this season included. */
  carry: FinanceCarry;
}

// ---------------------------------------------------------------------------
// One weekend's books
// ---------------------------------------------------------------------------

/**
 * The finance seed is derived from the RACE's seed and not from the season's.
 *
 * D-005 stores one seed per race; xor-ing a constant off it gives the books
 * their own stream, so adding or removing a money roll cannot shift a single
 * die inside the race itself.
 */
export const FINANCE_RACE_SEED_SALT = 0x00f1_1a5c;

export interface SeasonFinanceRaceInput {
  seasonId: string;
  /** The season's own record of the race whose books are being settled. */
  race: Pick<SeasonRaceResult, 'raceNumber' | 'raceSeed' | 'scheduled' | 'week' | 'race' | 'standings'>;
  /** Priced off the STARTING cards — every SAG table is keyed to those. */
  profiles: readonly FinanceDriverProfile[];
  state: FinanceState;
  previous?: PreviousRaceFacts;
  /**
   * THE SEASON'S LENGTH, not the number of races settled so far.
   *
   * Sagliano prorates yearly sponsorship, salary and automaker support across
   * the season, so this is the divisor. A caller settling race 7 of 36 that
   * passed 7 would pay out a seventh of the year's money every Sunday.
   */
  totalRaces: number;
  options?: SeasonFinanceOptions;
}

/**
 * ONE RACE WEEKEND'S MONEY — the step, so that it can be taken one at a time.
 *
 * `runSeasonFinance` below calls this thirty-six times and `seasonPlay.ts`'s
 * optional finance track calls it once per race as the season is played, which
 * is the same reason `runSeason` is a loop over `runSeasonRace`: one procedure,
 * so a season settled weekend by weekend cannot disagree with one settled in a
 * single call. `npm run seasonplay` compares the two event streams face to face.
 */
export function runSeasonFinanceRace(input: SeasonFinanceRaceInput): RaceFinanceResult {
  const { race } = input;
  return runRaceFinance({
    seasonId: input.seasonId,
    raceNumber: race.raceNumber,
    seed: race.raceSeed ^ FINANCE_RACE_SEED_SALT,
    track: trackCard(race.scheduled.trackId),
    week: race.week,
    race: race.race,
    standings: race.standings,
    profiles: input.profiles,
    state: input.state,
    previous: input.previous,
    options: { ...input.options, totalRaces: input.totalRaces },
  });
}

// ---------------------------------------------------------------------------
// The season
// ---------------------------------------------------------------------------

export function runSeasonFinance(input: SeasonFinanceInput): SeasonFinanceResult {
  const { season, roster } = input;
  const opts = input.options ?? {};
  const totalRaces = opts.totalRaces ?? season.races.length ?? SAGLIANO_BASELINE_RACES;
  const teamOf = input.teamOf ?? {};

  const profiles: FinanceDriverProfile[] = roster.map((c) => profileFromCard(c, teamOf[c.driverId]));
  const profileOf = new Map(profiles.map((p) => [p.driverId, p]));

  const events: MoneyEvent[] = [];
  const races: RaceFinanceResult[] = [];
  const sponsorChanges: (SponsorChange & { raceNumber: number })[] = [];

  // ---- pre-season: family wealth (SAG/25) ---------------------------------
  // D-EZ-91 — the contracts last winter left on the cars, re-priced off this
  // year's cards; a brand-new league opens with SAG/14's primaries.
  let state = openingFinanceState(profiles, input.carry?.contracts, season.seasonId);
  const dice = new Dice(season.seed ^ 0x5a91_1a70);
  events.push(...rollFamilyWealth({ seasonId: season.seasonId, profiles, dice, ownerOf: opts.ownerOf }));

  // ---- the thirty-six weekends -------------------------------------------
  let previous: PreviousRaceFacts | undefined;
  let gloomyEconomy = false;

  for (const race of season.races) {
    const result = runSeasonFinanceRace({
      seasonId: season.seasonId,
      race,
      profiles,
      state,
      previous,
      totalRaces,
      options: opts,
    });

    events.push(...result.events);
    races.push(result);
    for (const change of result.sponsorChanges) sponsorChanges.push({ ...change, raceNumber: race.raceNumber });
    state = result.state;
    previous = raceFactsForNextWeek(race.race);
    gloomyEconomy = result.gloomyEconomy; // the LAST race's reading is the one SAG/18 wants
  }

  // ---- off-season ---------------------------------------------------------
  const final = season.finalStandings;
  const appearances = tvAppearanceCounts(races);
  const seasonTallies = foldSeasonTallies(races);

  events.push(...payBonuses({ seasonId: season.seasonId, final, profileOf }));

  const royalties = computeRoyalties({
    final,
    profiles,
    appearances,
    tallies: seasonTallies,
  });
  events.push(...payRoyalties({ seasonId: season.seasonId, royalties, profileOf }));

  const renewals = decideRenewals({
    final,
    profiles,
    state,
    appearances,
    tallies: seasonTallies,
    gloomyEconomy,
    dice,
  });
  applyRenewals(state, renewals, profileOf, season.seasonId);

  const ledger = foldMoney(events);
  return {
    seasonId: season.seasonId,
    events,
    races,
    royalties,
    renewals,
    sponsorChanges,
    state,
    gloomyEconomy,
    ledger,
    carry: closeTheBooks({ carry: input.carry, ledger, state, ownerOf: opts.ownerOf, profiles }),
  };
}

/**
 * D-EZ-91 — roll this season's P&L into the banks that carry.
 *
 * A car's season P&L goes to whoever OWNED the car this season (`ownerOf`, the
 * shop) — not to wherever its driver goes next — because the owner paid its
 * bills. An independent's car is its own owner.
 */
export function closeTheBooks(args: {
  carry?: FinanceCarry;
  ledger: MoneyLedger;
  state: FinanceState;
  ownerOf?: Record<string, string>;
  profiles: readonly FinanceDriverProfile[];
}): FinanceCarry {
  const prior = args.carry ?? EMPTY_FINANCE_CARRY;
  const shopBankMb: Record<string, number> = { ...prior.shopBankMb };
  const driverWealthMb: Record<string, number> = { ...prior.driverWealthMb };
  for (const book of Object.values(args.ledger.books)) {
    if (book.account.kind === 'team') {
      const owner = args.ownerOf?.[book.account.id] ?? book.account.id;
      shopBankMb[owner] = (shopBankMb[owner] ?? 0) + book.balanceMb;
    } else if (book.account.kind === 'driver') {
      driverWealthMb[book.account.id] = (driverWealthMb[book.account.id] ?? 0) + book.balanceMb;
    }
  }
  const contracts: Record<string, DriverFinanceState> = {};
  for (const p of args.profiles) {
    const dfs = args.state.byDriver[p.driverId];
    if (!dfs) continue;
    contracts[p.driverId] = {
      ...dfs,
      primary: dfs.primary ? { ...dfs.primary } : null,
      major: dfs.major.map((c) => ({ ...c })),
      associate: dfs.associate.map((c) => ({ ...c })),
    };
  }
  return { contracts, shopBankMb, driverWealthMb, winters: prior.winters + 1 };
}

// ---------------------------------------------------------------------------
// Pre-season — family wealth (SAG/25)
// ---------------------------------------------------------------------------

function rollFamilyWealth(args: {
  seasonId: string;
  profiles: readonly FinanceDriverProfile[];
  dice: Dice;
  ownerOf?: Record<string, string>;
}): MoneyEvent[] {
  const { seasonId, profiles, dice, ownerOf } = args;
  const out: MoneyEvent[] = [];
  const ownersSpent = new Set<string>();

  // Stable order, so the "first one that is rolled" restriction is reproducible.
  const teams = [...new Set(profiles.map((p) => p.teamId))].sort();
  const driverOfTeam = new Map<string, FinanceDriverProfile>();
  for (const p of profiles) if (!driverOfTeam.has(p.teamId)) driverOfTeam.set(p.teamId, p);

  for (const teamId of teams) {
    const profile = driverOfTeam.get(teamId);
    if (!profile) continue;

    const roll = dice.sum2d6();
    if (!hasDeepPockets(roll.total)) continue;

    const owner = ownerOf?.[teamId] ?? teamId;
    if (ownersSpent.has(owner)) continue; // "only… the first one that is rolled"
    ownersSpent.add(owner);

    const amountMb = DEEP_POCKETS[profile.qualities];
    out.push(
      moneyEvent({
        seasonId,
        raceNumber: null,
        phase: 'preseason',
        account: teamAccount(teamId),
        amountMb,
        category: 'family-wealth',
        description: `The owner has deep pockets — ${amountMb} Mb of personal money goes into the team to keep it afloat.`,
        ruleCitation: `${CITE_FAMILY_WEALTH}${profile.qualities === 1 ? `; ${CITE_DEEP_POCKETS_TYPO}` : ''}`,
        dice: [{ kind: '2d6-sum', dice: roll.dice, total: roll.total, label: 'FAMILY WEALTH' }],
        modifiers: [{ name: 'qualities', value: profile.qualities }],
      }),
    );
  }

  return out;
}

// ---------------------------------------------------------------------------
// Off-season — bonus (SAG/27)
// ---------------------------------------------------------------------------

function payBonuses(args: {
  seasonId: string;
  final: SeasonStandings;
  profileOf: Map<string, FinanceDriverProfile>;
}): MoneyEvent[] {
  const out: MoneyEvent[] = [];
  for (const stat of args.final.drivers) {
    const amountMb = seasonBonusMb(stat.rank);
    if (amountMb <= 0) continue;
    const profile = args.profileOf.get(stat.driverId);
    if (!profile) continue;
    out.push(
      moneyEvent({
        seasonId: args.seasonId,
        raceNumber: null,
        phase: 'off-season',
        account: driverAccount(stat.driverId),
        amountMb,
        category: 'bonus',
        description: `${stat.name} finished the season ${ordinal(stat.rank)} in the Cup standings — a ${amountMb} Mb bonus.`,
        ruleCitation: CITE_BONUS,
        modifiers: [{ name: 'final Cup rank', value: stat.rank }],
      }),
    );
  }
  return out;
}

// ---------------------------------------------------------------------------
// Off-season — royalties (SAG/27-28)
// ---------------------------------------------------------------------------

export interface SeasonTally {
  mentionTotal: number;
  publicEyeTotal: number;
  positives: number;
  negatives: number;
}

/**
 * SAG/27 — fan appeal counts "the # of times the driver presented a positive,
 * negative or neutral image during Race Week", which is the public-eye group
 * counted as occurrences rather than summed as weights. Both readings are kept
 * because the two factors genuinely use them differently: the renewal factor
 * says "determine if the Driver had an overall positive or negative or zero
 * TOTAL", and the royalty factor says "the # of times".
 */
function foldSeasonTallies(races: readonly RaceFinanceResult[]): Record<string, SeasonTally> {
  const out: Record<string, SeasonTally> = {};
  for (const race of races) {
    for (const [driverId, tally] of Object.entries(race.weekTallies)) {
      const t = (out[driverId] ??= { mentionTotal: 0, publicEyeTotal: 0, positives: 0, negatives: 0 });
      t.mentionTotal += tally.mentionTotal;
      t.publicEyeTotal += tally.publicEyeTotal;
      for (const e of tally.effects) {
        if (e.group !== 'public-eye') continue;
        if (e.weight > 0) t.positives += 1;
        else if (e.weight < 0) t.negatives += 1;
      }
    }
  }
  return out;
}

function tvAppearanceCounts(races: readonly RaceFinanceResult[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const race of races) for (const id of race.tvAppearanceIds) out[id] = (out[id] ?? 0) + 1;
  return out;
}

/**
 * SAG/27 — "the frequency the driver was a Featured TV Driver… compared to the
 * league average for all drivers."
 *
 * The average is taken across every driver on the roster, including those who
 * never made the list, because that is what "all other Drivers" means and
 * excluding the zeroes would make "average" unreachable for half the field.
 */
function mediaStanding(count: number, average: number): 'above' | 'average' | 'below' {
  if (count > average) return 'above';
  if (count < average) return 'below';
  return 'average';
}

function computeRoyalties(args: {
  final: SeasonStandings;
  profiles: readonly FinanceDriverProfile[];
  appearances: Record<string, number>;
  tallies: Record<string, SeasonTally>;
}): RoyaltyBreakdown[] {
  const { final, profiles, appearances, tallies } = args;
  const totalAppearances = profiles.reduce((n, p) => n + (appearances[p.driverId] ?? 0), 0);
  const average = profiles.length > 0 ? totalAppearances / profiles.length : 0;

  const out: RoyaltyBreakdown[] = [];
  for (const profile of profiles) {
    const stat = final.byDriver[profile.driverId];
    if (!stat) continue;

    const count = appearances[profile.driverId] ?? 0;
    const tally = tallies[profile.driverId] ?? { mentionTotal: 0, publicEyeTotal: 0, positives: 0, negatives: 0 };

    const mediaPoints = royaltyMediaPoints(mediaStanding(count, average));
    const fanPoints = royaltyFanPoints(
      tally.positives > tally.negatives ? 'positive' : tally.negatives > tally.positives ? 'negative' : 'neutral',
    );
    const talentPoints = royaltyTalentPoints(profile.qualities);
    const performancePoints = royaltyPerformancePoints(stat.rank);

    const total = mediaPoints + fanPoints + talentPoints + performancePoints;
    const factor = total / ROYALTY_DIVISOR;
    const salaryMb = DRIVER_SALARY_YEARLY[profile.qualities];

    out.push({
      driverId: profile.driverId,
      name: profile.name,
      mediaPoints,
      fanPoints,
      talentPoints,
      performancePoints,
      total,
      factor,
      salaryMb,
      // A negative factor would have a sponsor invoicing the driver for the
      // privilege of selling his hat. Floored at zero: the guide never
      // contemplates it, and every worked example is positive.
      royaltiesMb: Math.max(0, factor * salaryMb),
      tvAppearances: count,
      leagueAverageAppearances: average,
    });
  }
  return out;
}

function payRoyalties(args: {
  seasonId: string;
  royalties: readonly RoyaltyBreakdown[];
  profileOf: Map<string, FinanceDriverProfile>;
}): MoneyEvent[] {
  const out: MoneyEvent[] = [];
  for (const r of args.royalties) {
    if (r.royaltiesMb <= 0) continue;
    const profile = args.profileOf.get(r.driverId);
    if (!profile) continue;

    const modifiers: Modifier[] = [
      { name: 'talent', value: r.talentPoints, note: `${profile.qualities} qualities` },
      { name: 'performance', value: r.performancePoints, note: 'Cup standings' },
      { name: 'media presence', value: r.mediaPoints, note: `${r.tvAppearances} TV weeks vs a league average of ${round2(r.leagueAverageAppearances)}` },
      { name: 'fan appeal', value: r.fanPoints, note: 'Race Week image' },
      { name: 'sum ÷ 6', value: round2(r.factor) },
      { name: 'current salary', value: r.salaryMb },
    ];

    out.push(
      moneyEvent({
        seasonId: args.seasonId,
        raceNumber: null,
        phase: 'off-season',
        account: driverAccount(r.driverId),
        amountMb: r.royaltiesMb,
        category: 'royalties',
        description: `${r.name}'s merchandise and endorsement royalties — ${r.total} points over six, times a ${r.salaryMb} Mb salary.`,
        ruleCitation: CITE_ROYALTIES,
        modifiers,
      }),
      moneyEvent({
        seasonId: args.seasonId,
        raceNumber: null,
        phase: 'off-season',
        account: teamAccount(profile.teamId),
        amountMb: r.royaltiesMb,
        category: 'royalties',
        description: `The team's matching share of ${r.name}'s royalties. Not taken out of his.`,
        ruleCitation: CITE_TEAM_ROYALTIES,
        modifiers,
      }),
    );
  }
  return out;
}

// ---------------------------------------------------------------------------
// Off-season — sponsorship renewal (SAG/17-19)
// ---------------------------------------------------------------------------

/** SAG/17 factor 1 — final rank in the racing cup series standings. */
function racingSuccessPoints(rank: number): number {
  if (rank === 1) return 50;
  if (rank <= 5) return 40;
  if (rank <= 10) return 30;
  if (rank <= 20) return 20;
  return -20;
}

function decideRenewals(args: {
  final: SeasonStandings;
  profiles: readonly FinanceDriverProfile[];
  state: FinanceState;
  appearances: Record<string, number>;
  tallies: Record<string, SeasonTally>;
  gloomyEconomy: boolean;
  dice: Dice;
}): RenewalDecision[] {
  const { final, profiles, state, appearances, tallies, gloomyEconomy, dice } = args;
  const totalAppearances = profiles.reduce((n, p) => n + (appearances[p.driverId] ?? 0), 0);
  const average = profiles.length > 0 ? totalAppearances / profiles.length : 0;

  const out: RenewalDecision[] = [];

  for (const profile of profiles) {
    const dfs = state.byDriver[profile.driverId];
    const stat = final.byDriver[profile.driverId];
    if (!dfs || !stat) continue;

    const tally = tallies[profile.driverId] ?? { mentionTotal: 0, publicEyeTotal: 0, positives: 0, negatives: 0 };
    const count = appearances[profile.driverId] ?? 0;
    const standing = mediaStanding(count, average);

    // Factors 1-4 and 6 are shared across every sponsorship this driver holds.
    // Only factor 5 — the sponsor's own relationship with the team — is re-rolled
    // per sponsor (SAG/19, explicit).
    const shared: RenewalFactor[] = [
      {
        name: 'racing success',
        points: racingSuccessPoints(stat.rank),
        note: `finished ${ordinal(stat.rank)} in the Cup standings`,
        ruleCitation: 'SAG/17 — factor 1',
      },
      {
        name: 'television exposure',
        points: standing === 'above' ? 10 : standing === 'below' ? -10 : 0,
        note: `${count} TV weeks against a league average of ${round2(average)}`,
        ruleCitation: 'SAG/17 — factor 2',
      },
      {
        name: 'sponsor mention',
        points: mentionFactorPoints(tally.mentionTotal),
        note: `net ${tally.mentionTotal >= 0 ? '+' : ''}${tally.mentionTotal} across the season`,
        ruleCitation: CITE_SPONSOR_MENTION,
      },
      {
        name: 'in the public eye',
        points: publicEyeFactorPoints(tally.publicEyeTotal),
        note: `net ${tally.publicEyeTotal >= 0 ? '+' : ''}${tally.publicEyeTotal} across the season`,
        ruleCitation: CITE_PUBLIC_EYE,
      },
      {
        name: 'the economy',
        points: gloomyEconomy ? -10 : 10,
        note: gloomyEconomy
          ? `a ${GLOOMY_ECONOMY_ROLL} came up in the season's last race`
          : 'no gloom in the final race of the year',
        ruleCitation: 'SAG/18 — factor 6',
      },
    ];

    const contracts: { tier: 'primary' | 'major' | 'associate'; index: number }[] = [];
    if (dfs.primary) contracts.push({ tier: 'primary', index: 0 });
    dfs.major.forEach((_, i) => contracts.push({ tier: 'major', index: i }));
    dfs.associate.forEach((_, i) => contracts.push({ tier: 'associate', index: i }));

    for (const contract of contracts) {
      const relationshipRoll = dice.sum2d6();
      const satisfied = relationshipRoll.total <= 6; // "2-6 satisfying, 7-12 disappointing"
      const factors: RenewalFactor[] = [
        ...shared,
        {
          name: "sponsor's relationship with the team",
          points: satisfied ? 10 : -10,
          note: satisfied
            ? 'the sponsor had a satisfying relationship with the team'
            : 'the sponsor had a disappointing relationship with the team',
          ruleCitation: 'SAG/18 — factor 5, rolled separately for every sponsorship',
        },
      ];

      const total = factors.reduce((n, f) => n + f.points, 0);
      const automatic = contract.tier === 'primary' && dfs.autoRenewPrimary;

      // SAG/19 — the primary renews on a positive OR ZERO sum; a major or an
      // associate needs a strictly positive one. Transcribed as printed.
      const renewed = automatic || (contract.tier === 'primary' ? total >= 0 : total > 0);

      const decision: RenewalDecision = {
        driverId: profile.driverId,
        name: profile.name,
        tier: contract.tier,
        factors,
        total,
        renewed,
        automatic,
        description: automatic
          ? `${profile.name}'s primary sponsor renewed automatically — Z54 came up during a Race Week.`
          : renewed
            ? `${profile.name}'s ${contract.tier} sponsor is staying. Value to the sponsor: ${total >= 0 ? '+' : ''}${total}.`
            : `${profile.name}'s ${contract.tier} sponsor is walking away. Value to the sponsor: ${total}.`,
      };

      // SAG/19 — a dropped primary is replaced automatically, one quality lower.
      if (contract.tier === 'primary' && !renewed && profile.qualities >= 2) {
        decision.replacedAtQualities = qualityTier(profile.qualities - 1);
        decision.description += ` He signs a new one at ${decision.replacedAtQualities}-quality money — ${SPONSORSHIP_YEARLY.primary[decision.replacedAtQualities]} Mb instead of ${SPONSORSHIP_YEARLY.primary[profile.qualities]}.`;
      }

      out.push(decision);
    }
  }

  return out;
}

/**
 * SAG/19 — carry the surviving contracts into next season, and replace a
 * dropped primary at the penalised rate. A one-quality driver whose primary
 * walks has nowhere lower to go, so he keeps his own tier; the guide's
 * automatic-replacement rule is written for "a Driver with two to five
 * qualities" and says nothing about the one-quality case.
 */
function applyRenewals(
  state: FinanceState,
  renewals: readonly RenewalDecision[],
  profileOf: Map<string, FinanceDriverProfile>,
  seasonId: string,
): void {
  for (const [driverId, dfs] of Object.entries(state.byDriver)) {
    const profile = profileOf.get(driverId);
    if (!profile) continue;
    const mine = renewals.filter((r) => r.driverId === driverId);

    const primary = mine.find((r) => r.tier === 'primary');
    if (primary && !primary.renewed) {
      const q = primary.replacedAtQualities ?? profile.qualities;
      const outgoing = dfs.primary?.sponsorName;
      dfs.primary = {
        tier: 'primary',
        pricedAtQualities: q,
        yearlyMb: SPONSORSHIP_YEARLY.primary[q],
        signedRace: 0,
        // D-EZ-96 — a new name on the hood, signed over the winter: its first
        // season is next season, so the opening state does not age it.
        sponsorName: sponsorNameFor(`${seasonId}|${driverId}|primary|winter`, new Set(outgoing ? [outgoing] : [])),
        seasonsHeld: 1,
        signedInWinter: true,
        // D-EZ-91 — the SAG/19 discount travels as an offset, so re-pricing it
        // off next year's card keeps it one tier under the driver's own.
        priceOffset: q - profile.qualities,
      };
    }

    const majorDecisions = mine.filter((r) => r.tier === 'major');
    dfs.major = dfs.major.filter((_, i) => majorDecisions[i]?.renewed ?? false);

    const associateDecisions = mine.filter((r) => r.tier === 'associate');
    dfs.associate = dfs.associate.filter((_, i) => associateDecisions[i]?.renewed ?? false);

    // A new season starts with a clean sheet on warnings and blackouts.
    dfs.primaryBlackoutRaces = 0;
    dfs.badMouthWarnings = 0;
    dfs.autoRenewPrimary = false;
  }
}

// ---------------------------------------------------------------------------
// Reading the books back
// ---------------------------------------------------------------------------

export interface TeamBookLine {
  teamId: string;
  balanceMb: number;
  incomeMb: number;
  expenseMb: number;
  byCategory: Record<string, number>;
  solvent: boolean;
}

export interface DriverBookLine {
  driverId: string;
  name: string;
  earningsMb: number;
  careerWinningsMb: number;
  salaryMb: number;
  royaltiesMb: number;
  bonusMb: number;
  finesMb: number;
}

export interface SeasonFinanceReport {
  teams: TeamBookLine[];
  drivers: DriverBookLine[];
  /** Teams that finished the season under water. */
  insolventTeams: number;
  /** Every Megabuck the league collected in fines and TV rights. */
  leagueBalanceMb: number;
}

/**
 * A statement, folded from the stream. Nothing here is stored; call it twice
 * and you get the same answer because the answer is a function of the events.
 */
export function seasonFinanceReport(
  result: SeasonFinanceResult,
  profiles: readonly FinanceDriverProfile[],
): SeasonFinanceReport {
  const ledger = foldMoney(result.events);

  const teams: TeamBookLine[] = [];
  const drivers: DriverBookLine[] = [];

  for (const book of Object.values(ledger.books)) {
    if (book.account.kind === 'team') {
      teams.push({
        teamId: book.account.id,
        balanceMb: book.balanceMb,
        incomeMb: book.incomeMb,
        expenseMb: book.expenseMb,
        byCategory: book.byCategory,
        solvent: book.balanceMb >= 0,
      });
    } else if (book.account.kind === 'driver') {
      const profile = profiles.find((p) => p.driverId === book.account.id);
      drivers.push({
        driverId: book.account.id,
        name: profile?.name ?? book.account.id,
        earningsMb: book.balanceMb,
        careerWinningsMb: book.careerWinningsMb,
        salaryMb: book.byCategory.salary ?? 0,
        royaltiesMb: book.byCategory.royalties ?? 0,
        bonusMb: book.byCategory.bonus ?? 0,
        finesMb: -(book.byCategory.fine ?? 0),
      });
    }
  }

  teams.sort((a, b) => b.balanceMb - a.balanceMb);
  drivers.sort((a, b) => b.earningsMb - a.earningsMb);

  return {
    teams,
    drivers,
    insolventTeams: teams.filter((t) => !t.solvent).length,
    leagueBalanceMb: ledger.books['league:league']?.balanceMb ?? 0,
  };
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]);
}

// ---------------------------------------------------------------------------
// Mapping our garage onto Sagliano's
// ---------------------------------------------------------------------------

/**
 * SAG/12 IS EXPLICIT AND WE DO NOT MATCH IT BY ACCIDENT:
 *
 *     "A Team can have only one Driver."
 *     "A Team Owner may own up to three teams (and a total of three Drivers)."
 *     "Teams are always treated separately in the rules for all game functions
 *      despite having two or three Teams with the same owner. A Team Owner of
 *      two or three Teams cannot switch his money from one of his Teams to the
 *      others, for example."
 *
 * Our field generator builds fourteen chartered shops of three drivers each
 * (`field.ts`). Read naively that is a Team with three Drivers, which Sagliano
 * forbids. Read correctly it is exactly his structure: our SHOP is his TEAM
 * OWNER, and each of its three cars is one of his single-driver TEAMS.
 *
 * That is not a cosmetic relabelling. It decides three things:
 *
 *   • Each car keeps its OWN books. A shop cannot bail out its third car with
 *     the first car's winnings — which is the sentence above, enforced.
 *   • Sponsorship, automaker support and salary are priced off ONE driver's
 *     quality count, which is what every table in the guide is keyed to.
 *   • Family wealth pays out once per OWNER, "on the first one that is rolled"
 *     — a restriction that means nothing unless owners span several teams.
 *
 * `financeTeamId` gives each driver his own Sagliano team; `ownerMap` gives the
 * shop back as the owner.
 */
export function financeTeamId(driverId: string): string {
  return `car-${driverId}`;
}

export function teamMap(roster: readonly DriverCard[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const card of roster) out[card.driverId] = financeTeamId(card.driverId);
  return out;
}

export function ownerMap(
  teams: readonly { teamId: string; driverIds: readonly string[] }[],
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const shop of teams) {
    for (const driverId of shop.driverIds) out[financeTeamId(driverId)] = shop.teamId;
  }
  return out;
}
